import { Component, ReactNode } from 'react';
import { Box, Button, theme, ThemeProvider, Typography } from '@rapid-recovery-agency-inc/sloth-ui-web';
import { getAwsRum } from '../monitoring';
import { connect } from 'react-redux';
import { AppState } from '../../redux/store';
import { FeatureFlagKey } from '@rapid-recovery-agency-inc/insightt-shared';
import { RumEvent } from '../analytics/rum';
import { getOwnProps } from '../utils';

const DYNAMIC_IMPORT_STRINGS = [
  'Unable to preload CSS',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
];

interface Props {
  children: ReactNode;
  featureFlags?: Partial<Record<FeatureFlagKey, boolean>>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    let stringError;
    try {
      stringError = JSON.stringify(getOwnProps(error));
    } catch {
      stringError = '';
    }

    if (!DYNAMIC_IMPORT_STRINGS.some((msg) => stringError.includes(msg))) {
      getAwsRum().recordEvent(RumEvent.ERROR_BOUNDARY, {
        error: error.message,
        stack: error.stack,
        string: stringError,
      });
    }

    // Update state so the next render shows the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    // Feature flags
    if (this.props.featureFlags?.WEB_ENABLE_ERROR_BOUNDARY_RELOAD) {
      if (
        error.message &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
          error.message.includes('Importing a module script failed'))
      ) {
        this.handleReload();
      }
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ThemeProvider theme={theme}>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="h3">Something went wrong</Typography>
            <Typography variant="h6">Please reload the page</Typography>
            <Button
              onClick={this.handleReload}
              size="large"
              sx={{
                mt: 5,
              }}
            >
              Reload
            </Button>
          </Box>
        </ThemeProvider>
      );
    }

    return this.props.children;
  }
}

const mapStateToProps = (state: AppState) => ({
  featureFlags: (state.system.session.featureFlagsMap ?? {}) as Record<FeatureFlagKey, boolean>,
});

export const ErrorBoundaryReload = connect(mapStateToProps)(ErrorBoundary);
